import type { ReactElement, ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import {
	NavigationType,
	useLocation,
	useNavigationType,
} from 'react-router-dom';

/**
 * Route-transition prototype (PD-14696): a ~110ms fade-in on PUSH
 * navigations so route changes read as a smooth handoff instead of a
 * hard cut. Deliberately minimal, per the ticket's constraints:
 *
 *  - PUSH only. Back/forward (POP) and redirects (REPLACE) stay
 *    instant — the native-feeling snap matters more than polish there,
 *    and the initial page load is a POP so first paint is never dimmed.
 *  - Fade-IN only, applied to the incoming route. There is no exit
 *    animation and nothing is deferred: the new route mounts, fetches,
 *    and accepts input immediately (opacity doesn't block hit-testing);
 *    only its first 110ms of paint ramps.
 *  - `prefers-reduced-motion` is respected twice — the JS guard here
 *    and the CSS media override on `.route-fade-in` (index.css).
 *
 * Adopted after design sign-off on the recorded prototype (2026-07-11).
 * To remove: unmount this wrapper in PortalLayout and drop the
 * index.css block; nothing else references it.
 */
export function RouteFadeContainer({
	children,
	className,
}: {
	children: ReactNode;
	/**
	 * Applied to the wrapper. PortalLayout passes `min-h-full` so a route
	 * that wants to fill the viewport can size against the space `main`
	 * actually has, rather than claiming a full 100dvh of its own and
	 * pushing the attribution footer out of view (PD-14409).
	 */
	className?: string;
}): ReactElement {
	const containerRef = useRef<HTMLDivElement>(null);
	const location = useLocation();
	const navigationType = useNavigationType();

	// useLayoutEffect so the class lands before the browser paints the new
	// route — with useEffect the first frame could flash at full opacity
	// and then dip, which reads as flicker instead of a fade.
	useLayoutEffect(() => {
		const el = containerRef.current;
		if (el == null || navigationType !== NavigationType.Push) {
			return;
		}
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			return;
		}
		// Restart the one-shot animation: drop the class, force a reflow so
		// the browser forgets the previous run, re-add.
		el.classList.remove('route-fade-in');
		void el.offsetWidth;
		el.classList.add('route-fade-in');
	}, [location.pathname, navigationType]);

	return (
		<div ref={containerRef} className={className}>
			{children}
		</div>
	);
}
